import { useSearchParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { resolveClientMarketplaceAttribution } from "@/utils/functions/marketplaceAttributionSession";

/**
 * Read `window.location.search` (without the leading `?`) when on the client.
 * Falls back to the SSR-safe value passed in (typically the result of
 * `useSearchParams()` from `next/navigation`).
 */
const readSearchString = (fallback: string): string => {
  if (typeof window === "undefined") return fallback;
  return window.location.search.replace(/^\?/, "");
};

const useHandleSearchParams = () => {
  // Source of truth for the FIRST render (SSR-safe). After mount, we switch to
  // a popstate-subscribed local state so URL writes via `replaceState` (which
  // Next's `useSearchParams` does not observe) can still re-trigger renders.
  const initialNextParams = useSearchParams();

  const [searchString, setSearchString] = useState<string>(() =>
    readSearchString(initialNextParams.toString()),
  );

  useEffect(() => {
    // Re-sync from window on mount in case the URL drifted between SSR
    // payload and hydration (e.g. extension-rewritten URLs).
    setSearchString(readSearchString(initialNextParams.toString()));

    const handlePopState = () => {
      setSearchString(readSearchString(""));
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
    // We intentionally subscribe once on mount; subsequent param changes are
    // delivered through the popstate dispatches below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchParams = useMemo(() => {
    const params = new URLSearchParams(searchString);
    const paramsObj: { [key: string]: string[] } = {};
    params.forEach((value, key) => {
      if (paramsObj[key]) {
        paramsObj[key].push(value);
      } else {
        paramsObj[key] = [value];
      }
    });
    return paramsObj;
  }, [searchString]);

  const currentUrlParams: string = searchString ? `?${searchString}` : "";

  // Notify in-process listeners (e.g. `SyncInstantSearchRefinementsFromUrl`,
  // `useCustomResultsPage`) that the URL changed. `history.replaceState` does
  // NOT fire `popstate` natively — only browser back/forward do — so without
  // this dispatch consumers that rely on URL → state sync would never run.
  // Mirrors the pattern already in use inside
  // `useCustomResultsPage.clearFilter` / `clearFilters`.
  const dispatchUrlChange = () => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  const setSearchParam = (key: string, value: string) => {
    const newParams = new URLSearchParams(currentUrlParams);
    newParams.append(key, value);

    const newQuery = newParams.toString();
    const newUrl = `${window.location.pathname}${newQuery ? `?${newQuery}` : ""}`;
    window.history.replaceState(null, "", newUrl);
    dispatchUrlChange();
  };

  const removeSearchParam = (key: string, value: string) => {
    const newParams = new URLSearchParams(currentUrlParams);
    const allValues = newParams.getAll(key);
    newParams.delete(key);
    allValues.forEach((paramValue) => {
      if (paramValue !== value) {
        newParams.append(key, paramValue);
      }
    });

    const newQuery = newParams.toString();
    const newUrl = `${window.location.pathname}${newQuery ? `?${newQuery}` : ""}`;
    window.history.replaceState(null, "", newUrl);
    dispatchUrlChange();
  };

  const clearSearchParams = () => {
    const attributionParams = resolveClientMarketplaceAttribution(
      window.location.search,
    );
    const newQuery = attributionParams.toString();
    const newUrl = `${window.location.pathname}${newQuery ? `?${newQuery}` : ""}`;
    window.history.replaceState(null, "", newUrl);
    dispatchUrlChange();
  };

  const clearSearchParamsExceptQuery = () => {
    const url = new URL(window.location.href);
    const newParams = new URLSearchParams();

    const query = url.searchParams.get("q");
    if (query) {
      newParams.set("q", query);
    }

    resolveClientMarketplaceAttribution(url.searchParams).forEach(
      (value, key) => {
        newParams.set(key, value);
      },
    );

    const newQuery = newParams.toString();
    const newUrl = `${window.location.pathname}${newQuery ? `?${newQuery}` : ""}`;
    window.history.replaceState(null, "", newUrl);
    dispatchUrlChange();
  };

  return {
    searchParams,
    setSearchParam,
    clearSearchParams,
    currentUrlParams,
    removeSearchParam,
    clearSearchParamsExceptQuery,
  };
};

export default useHandleSearchParams;
